import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, MessageSquare, Phone } from "lucide-react";
import { getPostBySlug, getAllPosts } from "@/blog/posts";
import { useSeo, SITE, localBusinessSchema } from "@/hooks/useSeo";
import { track } from "@/lib/analytics";

function formatDate(d) {
  try {
    return new Date(d + "T12:00:00").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

const SMS_HREF = 'sms:+17123109514?body=Gutters';
const TEL_HREF = 'tel:+17123109514';

function InlineQuoteCTA() {
  return (
    <aside
      className="not-prose my-12 bg-[#0A0A0A] text-white rounded-sm overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.18)]"
      data-testid="inline-quote-cta"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
        <div className="md:col-span-3 p-7 sm:p-9">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
            Ready when you are
          </div>
          <h3 className="font-display text-2xl sm:text-3xl leading-tight tracking-normal">
            Get a <span className="text-[#5FBE1F]">$100 cleaning quote</span> in under a minute.
          </h3>
          <p className="mt-3 text-sm text-[#C9C9C9] leading-relaxed">
            Flat $100 per home. Two neighbors on the same block? Split it for $150 total. Detached garage or shed? Add it for $40.
          </p>
        </div>
        <div className="md:col-span-2 bg-[#0F0F0F] p-7 sm:p-9 flex flex-col justify-center gap-3 border-t md:border-t-0 md:border-l border-[#1F1F1F]">
          <a
            href={SMS_HREF}
            onClick={() => track("text_gutters_click", { location: "blog_inline_cta" })}
            className="inline-flex items-center justify-center gap-2 bg-[#5FBE1F] text-white font-bold px-5 py-3 rounded-sm hover:bg-[#4A9818] transition-colors"
            data-testid="inline-cta-sms"
          >
            <MessageSquare className="w-4 h-4" />
            Text &quot;Gutters&quot;
          </a>
          <a
            href={TEL_HREF}
            onClick={() => track("phone_call_click", { location: "blog_inline_cta" })}
            className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-bold px-5 py-3 rounded-sm hover:bg-white hover:text-black transition-colors"
            data-testid="inline-cta-call"
          >
            <Phone className="w-4 h-4" />
            (712) 310-9514
          </a>
        </div>
      </div>
    </aside>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  // SEO must be called before any early return
  useSeo(
    post
      ? {
          // Differentiated <title> tag — H1 stays as post.title, but the
          // browser/SEO title adds brand + locale so they pull distinct weight.
          title: `${post.seo_title || post.title} | Glenwood Gutter Guy — SW Iowa`,
          description: post.seo_description || post.excerpt,
          ogImage: post.cover_image || SITE.defaultOg,
          url: `${SITE.url}/blog/${post.slug}`,
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.seo_description || post.excerpt,
            image: post.cover_image,
            datePublished: post.date,
            dateModified: post.date,
            author: {
              "@type": "Person",
              name: "Mike Harrison",
            },
            publisher: localBusinessSchema(),
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE.url}/blog/${post.slug}`,
            },
            keywords: (post.tags || []).join(", "),
          },
        }
      : {}
  );

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const all = getAllPosts();
  const idx = all.findIndex((p) => p.slug === slug);
  const prev = idx >= 0 ? all[idx + 1] : null; // older
  const next = idx > 0 ? all[idx - 1] : null; // newer

  return (
    <div className="font-body text-[#000000] bg-white min-h-screen">
      <div className="h-2 bg-[#5FBE1F]" />

      <header className="border-b border-[#E5E5E5]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="post-home-link">
            <img
              src="/logo-v2.png"
              alt="Glenwood Gutter Guy"
              className="h-14 w-auto object-contain"
            />
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A] hover:text-[#5FBE1F]"
            data-testid="post-back-blog"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All posts
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-10 sm:py-14">
        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-2 text-xs text-[#4A4A46] mb-4">
            <CalendarIcon className="w-3.5 h-3.5" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
            {post.tags && post.tags.length > 0 && (
              <>
                <span className="text-[#9CA09B]">·</span>
                <span className="uppercase tracking-[0.18em] text-[#5FBE1F] font-bold text-[10px]">
                  {post.tags[0]}
                </span>
              </>
            )}
          </div>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#000000] leading-tight tracking-normal">
            {post.title}
          </h1>

          {post.cover_image && (
            <div
              className="mt-8 aspect-[21/9] bg-cover bg-center rounded-sm border border-[#E5E5E5]"
              style={{ backgroundImage: `url('${post.cover_image}')` }}
              role="img"
              aria-label={post.cover_alt || post.title}
            />
          )}

          <div className="prose prose-neutral max-w-none mt-10
            prose-headings:font-display prose-headings:uppercase prose-headings:tracking-normal prose-headings:text-[#000000]
            prose-h2:text-2xl prose-h2:sm:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-t prose-h2:border-[#E5E5E5] prose-h2:pt-8
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-[#000000] prose-p:leading-relaxed prose-p:text-base
            prose-li:text-[#000000] prose-li:my-1
            prose-strong:text-[#000000] prose-strong:font-bold
            prose-a:text-[#5FBE1F] prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-4 prose-blockquote:border-[#5FBE1F] prose-blockquote:bg-[#F5F5F5] prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic
            prose-hr:border-[#E5E5E5]"
            data-testid="post-body"
          >
            {post.body.split(/^\s*\[\[CTA\]\]\s*$/m).map((segment, i, arr) => (
              <React.Fragment key={i}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{segment}</ReactMarkdown>
                {i < arr.length - 1 && <InlineQuoteCTA />}
              </React.Fragment>
            ))}
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-[#E5E5E5]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#9CA09B] font-bold mb-3">
                Tags
              </div>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span
                    key={t}
                    className="text-xs px-3 py-1 bg-[#F5F5F5] border border-[#E5E5E5] rounded-sm text-[#4A4A46]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div
            className="mt-10 pt-6 border-t border-[#E5E5E5] text-xs font-semibold uppercase tracking-[0.22em] text-[#4A4A46]"
            data-testid="post-last-reviewed"
          >
            Last reviewed:{" "}
            <time dateTime={post.date} className="text-[#0A0A0A]">
              {(() => {
                try {
                  return new Date(post.date + "T12:00:00").toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                } catch {
                  return post.date;
                }
              })()}
            </time>
          </div>

          {/* CTA block */}
          <div className="mt-12 bg-[#0A0A0A] text-white rounded-sm p-6 sm:p-8">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
              Need a hand?
            </div>
            <div className="font-display text-2xl sm:text-3xl text-white">
              Text &quot;Gutters&quot; to get on Mike&apos;s schedule.
            </div>
            <p className="mt-3 text-sm text-[#C9C9C9]">
              Local, friendly, no upsell. Serving Glenwood and Southwest Iowa.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={SMS_HREF}
                onClick={() => track("text_gutters_click", { location: "blog_bottom_cta", slug: post.slug })}
                className="inline-flex items-center gap-2 bg-[#5FBE1F] text-black font-bold px-6 py-3 rounded-sm hover:bg-[#4A9818] hover:text-white transition-colors"
                data-testid="post-text-cta"
              >
                <MessageSquare className="w-4 h-4" />
                Text &quot;Gutters&quot;
              </a>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 border border-white text-white font-bold px-6 py-3 rounded-sm hover:bg-white hover:text-black transition-colors"
                data-testid="post-form-cta"
              >
                Or fill the form <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Prev / next */}
          {(prev || next) && (
            <nav className="mt-12 pt-8 border-t border-[#E5E5E5] grid grid-cols-1 sm:grid-cols-2 gap-6">
              {prev ? (
                <Link
                  to={`/blog/${prev.slug}`}
                  className="group block"
                  data-testid="post-prev"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#9CA09B] font-bold mb-2">
                    ← Older
                  </div>
                  <div className="font-display text-lg text-[#000000] group-hover:text-[#5FBE1F]">
                    {prev.title}
                  </div>
                </Link>
              ) : <div />}
              {next ? (
                <Link
                  to={`/blog/${next.slug}`}
                  className="group block sm:text-right"
                  data-testid="post-next"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#9CA09B] font-bold mb-2">
                    Newer →
                  </div>
                  <div className="font-display text-lg text-[#000000] group-hover:text-[#5FBE1F]">
                    {next.title}
                  </div>
                </Link>
              ) : <div />}
            </nav>
          )}
        </motion.article>
      </main>

      <footer className="bg-[#0A0A0A] text-[#C9C9C9] py-8 mt-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center text-xs">
          <div>© {new Date().getFullYear()} Glenwood Gutter Guy · Mike Harrison</div>
          <Link to="/" className="hover:text-white">glenwoodgutterguy.com</Link>
        </div>
      </footer>
    </div>
  );
}
